import React from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import {
  CheckCircle2,
  MapPin,
  Bed,
  CreditCard,
  Flag,
  Calendar,
  Ship,
  Car,
  Mail,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSiteTexts } from "@/cms/useSiteText";

function wib(t) {
  return t ? `${t} WIB` : "—";
}

export default function PackageConfirmationView({ confirmation }) {
  const navigate = useNavigate();
  const t = useSiteTexts({
    heading: "confirmation.heading",
    subtext: "confirmation.subtext",
    reservationLabel: "confirmation.reservationLabel",
    sectionPackage: "confirmation.sectionPackage",
    sectionGolfRounds: "confirmation.sectionGolfRounds",
    sectionFerry: "confirmation.sectionFerry",
    sectionLandTransport: "confirmation.sectionLandTransport",
    sectionAccommodation: "confirmation.sectionAccommodation",
    totalPaidLabel: "confirmation.totalPaidLabel",
    paymentMethodLabel: "confirmation.paymentMethodLabel",
    homeButton: "confirmation.homeButton",
    emailNote: "confirmation.emailNote",
  });
  const {
    selectedCourses,
    selectedAccommodation,
    checkInDate,
    checkInTime,
    checkOutDate,
    selectedFerry,
    selectedFerryTime,
    selectedOutboundFerryTime,
    selectedTransport,
    selectedTransportTime,
    selectedOutboundTransport,
    selectedOutboundTransportTime,
    selectedPackage,
    paymentMethod,
    grandTotal,
    priceBreakdown,
    discountAmount,
    appliedCoupon,
  } = confirmation;
  const nights = selectedPackage?.nights ?? 1;
  const golfTotal = priceBreakdown?.golfTotal ?? 0;
  const ferryTotal = priceBreakdown?.ferryTotal ?? 0;
  const transportTotal = priceBreakdown?.transportTotal ?? 0;
  const accommodationTotal = priceBreakdown?.accommodationTotal ?? 0;

  return (
    <div className="flex flex-col items-center py-4 mt-6">
      <motion.div
        initial={{ scale: 0.92, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 0.5, type: "spring", stiffness: 200 }}
        className="w-full max-w-lg"
      >
        {/* ── Success hero ── */}
        <div className="bg-[#0B2421] rounded-3xl px-8 py-10 text-center mb-5 relative overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/5" />
          <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/5" />

          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 260 }}
            className="w-20 h-20 rounded-full bg-[#D4AF37] flex items-center justify-center mx-auto mb-5"
          >
            <CheckCircle2 className="h-10 w-10 text-[#0B2421]" />
          </motion.div>

          <h1 className="text-white font-bold text-3xl mb-2">{t.heading}</h1>
          <p className="text-white/60 text-sm mb-5">{t.subtext}</p>

          {/* <div className="inline-flex items-center gap-2 bg-[#D4AF37]/15 border border-[#D4AF37]/40 rounded-full px-5 py-2">
            <span className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase">
              {t.reservationLabel}
            </span>
            <span className="text-[#D4AF37] font-bold text-sm">
              {reservationNumber}
            </span>
          </div> */}
        </div>

        {/* ── Summary card ── */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden mb-5">
          {/* Package header */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#0B2421] flex items-center justify-center shrink-0">
              <Calendar className="h-4 w-4 text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-widest">
                {t.sectionPackage}
              </p>
              <p className="text-[#0B2421] font-bold text-sm">
                {selectedPackage?.rounds} Round
                {selectedPackage?.rounds > 1 ? "s" : ""} / {nights} Night
                {nights > 1 ? "s" : ""}
              </p>
            </div>
          </div>

          {/* Golf rounds */}
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Flag className="h-3.5 w-3.5 text-[#D4AF37]" />
                <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-widest">
                  {t.sectionGolfRounds}
                </p>
              </div>
              <span className="text-[#0B2421] font-bold text-sm">
                SGD {golfTotal}
              </span>
            </div>
            <div className="space-y-2">
              {selectedCourses.map((course, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#0B2421] flex items-center justify-center shrink-0">
                    <span className="text-[#D4AF37] text-[9px] font-bold">
                      {i + 1}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 min-w-0">
                    <MapPin className="h-3 w-3 text-gray-400 shrink-0" />
                    <span className="text-[#0B2421] text-sm font-semibold truncate">
                      {course.name}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Ferry */}
          {selectedFerry && (
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <Ship className="h-3.5 w-3.5 text-[#D4AF37]" />
                <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-widest">
                  {t.sectionFerry}
                </p>
              </div>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[#0B2421] font-bold text-sm">
                    {selectedFerry.title}
                  </p>
                  <p className="text-gray-400 text-xs">
                    {selectedFerry.originport} ⇄ {selectedFerry.destination}
                  </p>
                  {selectedFerryTime && (
                    <p className="text-gray-400 text-xs mt-0.5">
                      Inbound departs {wib(selectedFerryTime)}
                    </p>
                  )}
                  {selectedOutboundFerryTime && (
                    <p className="text-gray-400 text-xs mt-0.5">
                      Outbound departs {wib(selectedOutboundFerryTime)}
                    </p>
                  )}
                </div>
                <span className="text-[#0B2421] font-bold text-sm shrink-0">
                  SGD {ferryTotal}
                </span>
              </div>
            </div>
          )}

          {/* Land Transport */}
          {selectedTransport && (
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <Car className="h-3.5 w-3.5 text-[#D4AF37]" />
                <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-widest">
                  {t.sectionLandTransport}
                </p>
              </div>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[#0B2421] font-bold text-sm">
                    {selectedTransport.title}
                    {selectedTransport.type
                      ? ` · ${selectedTransport.type}`
                      : ""}
                  </p>
                  {selectedTransportTime && (
                    <p className="text-gray-400 text-xs mt-0.5">
                      Inbound pickup {wib(selectedTransportTime)}
                    </p>
                  )}
                  {selectedOutboundTransportTime && (
                    <p className="text-gray-400 text-xs mt-0.5">
                      Return pickup {wib(selectedOutboundTransportTime)}
                      {selectedOutboundTransport
                        ? ` · ${selectedOutboundTransport.title}`
                        : ""}
                    </p>
                  )}
                </div>
                <span className="text-[#0B2421] font-bold text-sm shrink-0">
                  SGD {transportTotal}
                </span>
              </div>
            </div>
          )}
          {/* Accommodation */}
          {selectedAccommodation && (
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <Bed className="h-3.5 w-3.5 text-[#D4AF37]" />
                <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-widest">
                  {t.sectionAccommodation}
                </p>
              </div>
              <div className="flex items-start justify-between gap-3 w-full">
                <div className="flex items-center gap-3">
                  {selectedAccommodation.image && (
                    <img
                      src={selectedAccommodation.image}
                      alt={selectedAccommodation.title}
                      className="w-12 h-12 rounded-xl object-cover shrink-0"
                    />
                  )}
                  <div>
                    <p className="text-[#0B2421] font-bold text-sm">
                      {selectedAccommodation.title}
                    </p>
                    <p className="text-gray-400 text-xs">
                      {nights} night{nights > 1 ? "s" : ""} · SGD{" "}
                      {selectedAccommodation.price} per room per night
                    </p>
                    {checkInDate && checkOutDate && (
                      <p className="text-gray-400 text-xs mt-0.5">
                        {format(new Date(checkInDate), "dd MMM")} –{" "}
                        {format(new Date(checkOutDate), "dd MMM yyyy")}
                      </p>
                    )}
                    {checkInTime && (
                      <p className="text-gray-400 text-xs mt-0.5">
                        Check-in {wib(checkInTime)} (fixed)
                      </p>
                    )}
                  </div>
                </div>
                <span className="text-[#0B2421] font-bold text-sm shrink-0">
                  SGD {accommodationTotal}
                </span>
              </div>
            </div>
          )}

          {/* Payment summary */}
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">{t.totalPaidLabel}</span>
              <span className="text-[#0B2421] font-bold text-lg">
                SGD {grandTotal}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-sm flex items-center gap-1.5">
                <CreditCard className="h-3.5 w-3.5" />
                {t.paymentMethodLabel}
              </span>
              <span className="text-[#0B2421] font-semibold text-sm">
                {paymentMethod}
              </span>
            </div>
          </div>
        </div>

        {/* ── Email delivery notice ── */}
        <div className="flex items-start gap-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-2xl px-5 py-4 mb-5">
          <div className="w-9 h-9 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
            <Mail className="h-4 w-4 text-[#B8860B]" />
          </div>
          <p className="text-[#0B2421] text-sm font-medium leading-relaxed pt-1">
            {t.emailNote}
          </p>
        </div>

        {/* ── CTA ── */}
        <button
          onClick={() => navigate("/")}
          className="w-full py-4 bg-[#D4AF37] hover:bg-[#C9A430] text-[#0B2421] font-bold rounded-2xl transition-colors text-base shadow-md hover:shadow-lg"
        >
          {t.homeButton}
        </button>
      </motion.div>
    </div>
  );
}
